/* eslint-disable react/function-component-definition */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import { REACT_APP_API_CAFE_EXPORT } from "../../redux/apiConstants";
import styles from "./CafeExportModal.module.scss";

const EXPORT_FORMATS = [
  { key: "csv", label: "CSV", icon: "fa-file-csv", description: "Comma-separated values" },
  { key: "xlsx", label: "Excel", icon: "fa-file-excel", description: "Microsoft Excel format" },
  { key: "json", label: "JSON", icon: "fa-file-code", description: "JavaScript Object Notation" },
];

const CafeExportModal = ({ show, handleClose }) => {
  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [loading, setLoading] = useState(false);
  const token = getCookies("Token");

  const resetState = () => {
    setSelectedFormat("csv");
    setLoading(false);
  };

  const handleCloseModal = () => {
    resetState();
    handleClose();
  };

  const handleExport = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${REACT_APP_API_CAFE_EXPORT}?format=${selectedFormat}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `cafes-export.${selectedFormat}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Export completed successfully!");
      handleCloseModal();
    } catch (error) {
      console.error("Export error:", error);
      const message =
        error.response?.data?.message || "Export failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      centered
      className={styles.exportModal}
    >
      <div className={styles.modalHeader}>
        <h5 className={styles.modalTitle}>
          <i className="fa fa-download" />
          Export Cafe Data
        </h5>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleCloseModal}
          aria-label="Close modal"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <Modal.Body className={styles.modalBody}>
        {loading ? (
          <div className={styles.loadingBox}>
            <Spinner animation="border" className={styles.spinner} />
            <h5>Exporting Cafes...</h5>
            <p>Please wait while we prepare your export file.</p>
          </div>
        ) : (
          <div className={styles.formatSection}>
            <h6 className={styles.sectionTitle}>
              <i className="fa fa-file-export" />
              Select Export Format
            </h6>
            <div className={styles.formatOptions}>
              {EXPORT_FORMATS.map((format) => (
                <button
                  key={format.key}
                  type="button"
                  className={`${styles.formatBtn} ${
                    selectedFormat === format.key ? styles.active : ""
                  }`}
                  onClick={() => setSelectedFormat(format.key)}
                  aria-label={`Export as ${format.label}`}
                >
                  <i className={`fa ${format.icon}`} />
                  <span className={styles.formatLabel}>{format.label}</span>
                  <span className={styles.formatDesc}>{format.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={handleCloseModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className={styles.primaryBtn}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <i className="fa fa-download" />
              Export {selectedFormat.toUpperCase()}
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
};

CafeExportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default CafeExportModal;
